// Must run before the router resolves its first navigation, so it sees the
// pristine query string from the legacy-domain redirect (see rebrand-notice).
import "@fontsource/plus-jakarta-sans/400.css";
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/800.css";
import { library } from "@fortawesome/fontawesome-svg-core";
import "@fortawesome/fontawesome-svg-core/styles.css";
import {
    faGoogle,
    faJs,
    faMarkdown,
    faPython,
} from "@fortawesome/free-brands-svg-icons";
import {
    faArrowAltCircleDown,
    faComments,
    faFile,
    faFileAlt,
    faFileArchive,
    faFilePdf,
    faImage,
    faWindowClose,
} from "@fortawesome/free-regular-svg-icons";
import {
    faAlignLeft,
    faAngleLeft,
    faAngleRight,
    faBold,
    faBuilding,
    faCheck,
    faCode,
    faCog,
    faCopy,
    faDatabase,
    faDisplay,
    faEdit,
    faFile as faFileSolid,
    faFolder,
    faFolderOpen,
    faHistory,
    faIndent,
    faItalic,
    faLayerGroup,
    faLink,
    faList,
    faListOl,
    faOutdent,
    faProjectDiagram,
    faQuestion,
    faQuoteLeft,
    faRedo,
    faSearch,
    faServer,
    faShield,
    faStrikethrough,
    faTasks,
    faTimes,
    faUnderline,
    faUndo,
    faVial,
    faVideo,
    faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import * as Sentry from "@sentry/vue";

import mitt from "mitt";
import { createApp } from "vue";

import App from "@/App.vue";
import { useOrganizationStore } from "@/composables/organization-manager";
import "@/firebase";
import "@/lib/rebrand-redirect";
import { pinia } from "@/pinia";
import router from "@/router";
import FontAwesomeButton from "@/shared/FontAwesomeButton.vue";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { useOrganizationsStore } from "@/stores/organizations-store";
import { useSteksStore } from "@/stores/steks-store";
import { useUserStore } from "@/stores/user-store";

import { initPrimeVue } from "./primevue/init";

const emitter = mitt();

export const app = createApp(App);
initPrimeVue(app);
app.use(router);

app.use(pinia);

if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.VITE_SENTRY_ENV) {
    Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENV,
        integrations: [Sentry.replayIntegration()],

        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

// Mitt
app.config.globalProperties.emitter = emitter;

app.component("FontAwesomeIcon", FontAwesomeIcon);
app.component("FontAwesomeButton", FontAwesomeButton);
library.add(
    faAlignLeft,
    faAngleLeft,
    faAngleRight,
    faArrowAltCircleDown,
    faBold,
    faBuilding,
    faCheck,
    faCode,
    faCog,
    faComments,
    faCopy,
    faDatabase,
    faEdit,
    faFile,
    faFileAlt,
    faFileArchive,
    faFilePdf,
    faFileSolid,
    faFolder,
    faFolderOpen,
    faGoogle,
    faHistory,
    faImage,
    faIndent,
    faItalic,
    faJs,
    faLayerGroup,
    faLink,
    faList,
    faListOl,
    faMarkdown,
    faOutdent,
    faProjectDiagram,
    faPython,
    faQuestion,
    faQuoteLeft,
    faRedo,
    faSearch,
    faServer,
    faDisplay,
    faShield,
    faStrikethrough,
    faTasks,
    faTimes,
    faUnderline,
    faUndo,
    faVial,
    faVideo,
    faWandMagicSparkles,
    faWindowClose
);

// Warm the boot-critical stores before mounting. Their immediate watchers
// fire the boot request burst (auth flip -> org list -> org resolution ->
// steks, /user/me) but only run once the store is instantiated — and
// without this, that first happens deep in the component tree, so the
// burst trails the mount/render/patch work instead of overlapping it.
// runWithContext provides the app's inject context (router, pinia) outside
// a component. Only stores free of component lifecycle hooks belong here:
// stek-manager and entity-store hang fetches off onMounted and would
// silently skip them if instantiated outside a component.
app.runWithContext(() => {
    useAuthenticationStore();
    useOrganizationsStore();
    useOrganizationStore();
    useSteksStore();
    useUserStore();
});

app.mount("#app");
